import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { salesOrderService } from "../services/svc_salesorders";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest, ListRequestSchemaV2 } from "../types/common/commonTypes";
import { OrderStatusUpdate } from "../types/combinedTypes";

export const useTSQSalesOrderMin = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.salesorder.listv2(null, "min"),
    queryFn: () => salesOrderService.listmin(),
    enabled: enabled,
  });
};

export const useTSQSalesOrderList = (params: ListRequest) => {
  return useQuery({
    queryKey: queryKeys.salesorder.list(params),
    queryFn: () => salesOrderService.list(params),
    placeholderData: keepPreviousData,
  });
};

export const useTSQSalesOrdersByCustomer = (
  params: ListRequestSchemaV2,
  source: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.salesorder.listv2(params, source),
    queryFn: () => salesOrderService.listByCustomer(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQSalesOrder = (id: string | null, enabled: boolean) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.salesorder.detail(id!),
    queryFn: () => salesOrderService.getById(id!),
    enabled: !!id && enabled,
  });

  const invalidate = () => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.salesorder.detail(id!),
    });
  };

  return {
    ...query,
    invalidate,
  };
};

export const useTSMCheckForDupePO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {cust_po: string, exclude_so: string[]}) => salesOrderService.checkDupePO(data),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Failed to check for duplicate PO:", error);
    },
  });
}

export const useTSMCreateSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: salesOrderService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesorder.lists() });
    },
    onError: (error) => {
      console.error("Failed to create SO:", error);
    },
  });
};

export const useTSMPostProcessSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => salesOrderService.postProcess(id),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Failed to perform SO Post Processing:", error);
    },
  });
};

export const useTSMRegenSODocs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => salesOrderService.regenDoc(id),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Failed to regen SO Documents:", error);
    },
  });
};

export const useTSMFulfillSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      salesOrderService.fulfill(id, data),
    onSuccess: (fulfilledSalesOrder) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesorder.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.salesorder.detail(fulfilledSalesOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to fulfill SO:", error);
    },
  });
};

export const useTSMUpdateSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      salesOrderService.update(id, data),
    onSuccess: (updateSalesOrder) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesorder.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.salesorder.detail(updateSalesOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to update SO:", error);
    },
  });
};

export const useTSMUpdateSalesOrderApproval = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      salesOrderService.updateApproval(id, data),
    onSuccess: (updatedSalesOrder) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesorder.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.salesorder.detail(updatedSalesOrder.id),
      });
    },
    onError: (error) => {
      console.error("Failed to update SO Approval:", error);
    },
  });
};

export const useTSMStatusUpdateSO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: OrderStatusUpdate }) =>
      salesOrderService.statusUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.salesorder.all,
      });
    },
    onError: (error) => {
      console.error("Failed to Update SO Status:", error);
    },
  });
};

export const useTSMCompleteSO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: salesOrderService.markComplete,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.salesorder.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to mark SO Complete:", error);
    },
  });
};

export const useTSMVoidSO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: salesOrderService.markVoid,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.salesorder.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to mark SO Void:", error);
    },
  });
};

export const useTSMDeactivateSO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: salesOrderService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesorder.lists() });
    },
    onError: (error) => {
      console.error("Failed to deactivate SO:", error);
    },
  });
};

export const useTSMReactivateSO = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: salesOrderService.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesorder.lists() });
    },
    onError: (error) => {
      console.error("Failed to reactivate SO:", error);
    },
  });
};
